/**
 * Instance setup wizard — schema-driven form + review (CUST-1).
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getInstance } from '@/services/aiStudioAgentService';
import {
  getInstanceSetupStatus,
  ingestInstanceSetupFile,
  saveInstanceSetup,
  type InstanceSetupStatusDto,
} from '@/services/aiStudioInstanceSetupService';
import { getInstanceSetupSchema } from '@/services/aiStudioInstanceSetupSchemaService';
import type { InstanceSetupSchema } from '@/types/aiStudioAgent';
import { readSetupFieldValues } from './setupFieldValues';
import type { SetupWizardStep } from './types';

export interface UseInstanceSetupWizardResult {
  status: InstanceSetupStatusDto | null;
  schema: InstanceSetupSchema | null;
  step: SetupWizardStep;
  fieldValues: Record<string, unknown>;
  loading: boolean;
  saving: boolean;
  uploadingFieldKey: string | null;
  errorKey: string | null;
  statusMessageKey: string | null;
  setStep: (step: SetupWizardStep) => void;
  setFieldValue: (key: string, value: unknown) => void;
  saveFields: () => Promise<boolean>;
  uploadFile: (fieldKey: string, file: File) => Promise<boolean>;
  completeSetup: () => Promise<boolean>;
  goToInstance: () => void;
  /** @deprecated Use fieldValues — kept for assist patch compatibility. */
  site: { name: string; location: string };
  /** @deprecated Use setFieldValue */
  setSiteField: (field: 'name' | 'location', value: string) => void;
  /** @deprecated Use saveFields */
  saveSite: () => Promise<boolean>;
}

export function useInstanceSetupWizard(instanceId: string | undefined): UseInstanceSetupWizardResult {
  const navigate = useNavigate();
  const [status, setStatus] = useState<InstanceSetupStatusDto | null>(null);
  const [schema, setSchema] = useState<InstanceSetupSchema | null>(null);
  const [step, setStep] = useState<SetupWizardStep>('form');
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFieldKey, setUploadingFieldKey] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [statusMessageKey, setStatusMessageKey] = useState<string | null>(null);

  useEffect(() => {
    if (!instanceId) {
      setLoading(false);
      setErrorKey('aiStudio.instanceSetup.notFound');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrorKey(null);

    Promise.all([
      getInstanceSetupStatus(instanceId),
      getInstanceSetupSchema(instanceId),
      getInstance(instanceId),
    ])
      .then(([loadedStatus, loadedSchema, instance]) => {
        if (cancelled) return;
        setStatus(loadedStatus);
        setSchema(loadedSchema);
        setFieldValues(readSetupFieldValues(instance.instanceSetup ?? {}));
        if (loadedStatus.status === 'ready') {
          setStep('review');
        } else if (loadedStatus.steps.fields) {
          setStep('review');
        }
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.instanceSetup.notFound');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  const setFieldValue = useCallback((key: string, value: unknown) => {
    setFieldValues((current) => ({ ...current, [key]: value }));
  }, []);

  const buildSavePayload = useCallback(() => {
    const fields: Record<string, unknown> = {};
    for (const field of schema?.provisionFields ?? []) {
      const raw = fieldValues[field.key];
      if (raw === undefined) continue;
      if (field.type === 'json' && typeof raw === 'string') {
        try {
          fields[field.key] = raw.trim() ? JSON.parse(raw) : undefined;
        } catch {
          fields[field.key] = raw;
        }
      } else {
        fields[field.key] = raw;
      }
    }
    return { fields };
  }, [fieldValues, schema?.provisionFields]);

  const saveFields = useCallback(async (): Promise<boolean> => {
    if (!instanceId) return false;
    setSaving(true);
    setErrorKey(null);
    try {
      const next = await saveInstanceSetup(instanceId, buildSavePayload());
      setStatus(next);
      setStep('review');
      return true;
    } catch {
      setErrorKey('aiStudio.instanceSetup.saveFailed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [instanceId, buildSavePayload]);

  const uploadFile = useCallback(
    async (fieldKey: string, file: File): Promise<boolean> => {
      if (!instanceId) return false;
      setUploadingFieldKey(fieldKey);
      setErrorKey(null);
      setStatusMessageKey(null);
      try {
        const next = await ingestInstanceSetupFile(instanceId, file, { fieldKey });
        setStatus(next);
        const instance = await getInstance(instanceId);
        setFieldValues(readSetupFieldValues(instance.instanceSetup ?? {}));
        setStatusMessageKey('aiStudio.instanceSetup.uploadSuccess');
        return true;
      } catch {
        setErrorKey('aiStudio.instanceSetup.uploadFailed');
        return false;
      } finally {
        setUploadingFieldKey(null);
      }
    },
    [instanceId]
  );

  const completeSetup = useCallback(async (): Promise<boolean> => {
    if (!instanceId) return false;
    setSaving(true);
    setErrorKey(null);
    try {
      const next = await saveInstanceSetup(instanceId, {
        ...buildSavePayload(),
        markComplete: true,
      });
      setStatus(next);
      setStatusMessageKey('aiStudio.instanceSetup.completeSuccess');
      return true;
    } catch {
      setErrorKey('aiStudio.instanceSetup.saveFailed');
      return false;
    } finally {
      setSaving(false);
    }
  }, [instanceId, buildSavePayload]);

  const goToInstance = useCallback(() => {
    if (instanceId) {
      navigate(`/ai-studio/instances/${instanceId}`);
    }
  }, [instanceId, navigate]);

  const site = {
    name: typeof fieldValues['instance_display_name'] === 'string'
      ? fieldValues['instance_display_name']
      : status?.summary.siteName ?? '',
    location: typeof fieldValues['instance_location'] === 'string'
      ? fieldValues['instance_location']
      : status?.summary.siteLocation ?? '',
  };

  const setSiteField = useCallback((field: 'name' | 'location', value: string) => {
    const key = field === 'name' ? 'instance_display_name' : 'instance_location';
    setFieldValue(key, value);
  }, [setFieldValue]);

  const saveSite = saveFields;

  return {
    status,
    schema,
    step,
    fieldValues,
    loading,
    saving,
    uploadingFieldKey,
    errorKey,
    statusMessageKey,
    setStep,
    setFieldValue,
    saveFields,
    uploadFile,
    completeSetup,
    goToInstance,
    site,
    setSiteField,
    saveSite,
  };
}
